/**
 * RWBR — D-018, THE PAUSE.
 *
 * "Manual dice entry (D-018) — do it properly or not at all; it means `Dice`
 *  becomes a source you can feed, every roll gets marked as hand-entered in the
 *  ledger, and the turn loop has to pause mid-turn. Not a text box."
 *                                          — claude/HANDOFF_next_session.md
 *
 * `dice.ts` owns the feed and the marking. This file owns the third thing, and
 * it is the one the job was deferred twice for: STOPPING IN THE MIDDLE OF A
 * TURN, saying what is being asked for, taking the face, and carrying on from
 * exactly there.
 *
 * ===========================================================================
 * THE ARCHITECTURE, AND WHY IT IS THIS ONE
 * ===========================================================================
 *
 * Three options were on the table.
 *
 * ── 1. A PRE-FED QUEUE. The player types in a turn's worth of dice up front
 *      and the engine eats them. Rejected, and the handoff rejects it in
 *      advance: it is not a pause. You cannot know up front how many dice a
 *      turn will want (a turn's dice count depends on what the earlier dice
 *      said — one RACE CAM total decides between a four-die turn and a
 *      forty-die one), so "a turn's worth" is not a quantity that exists.
 *
 * ── 2. A GENERATOR / COROUTINE. Make the turn resolution a generator that
 *      yields "I need a die" and is sent the face back. This is the textbook
 *      answer and it was measured against the code rather than assumed:
 *
 *        • A die is needed roughly EIGHT frames deep — `runRace` → `runTurn` →
 *          `resolveQualityChallenges` → the per-car challenge → `resolveTrouble`
 *          → the escape check → `Dice.sum2d6` → `Dice.d`.
 *        • JavaScript generators do not compose through ordinary calls. Every
 *          frame between the `yield` and the driver has to become a generator
 *          and every call site has to become `yield*`. That is not the eight
 *          frames of one path; it is EVERY path, because any of them can roll.
 *          Counted: ~25 dice-consuming call sites in `turn.ts` alone, across 30
 *          rule modules, plus `.map`/`.filter`/`.flatMap` callbacks that a
 *          `yield*` cannot cross at all and that would have to be unrolled into
 *          `for` loops by hand.
 *        • Every one of those rewrites is a chance to change a rule's behaviour
 *          in a way the 2,400-test suite would not catch, against a
 *          determinism guarantee that must not regress. That trade is bad.
 *
 * ── 3. REPLAY-BASED CONTINUATION — what is built here.
 *
 *      `runRace` is a pure function of `(setup, options)`. Nothing in the race
 *      engine reads a clock, a global, or `Math.random`; the dice are built
 *      inside from the seed, and now optionally from a fed queue that also
 *      arrives as an argument. Therefore:
 *
 *          runRace(setup, {...options, dice: {faces: F}})
 *
 *      is a FUNCTION OF F. Feed it the faces you have; the engine runs to the
 *      first die it wants that F does not contain and throws `DiceNeeded` from
 *      exactly that point, carrying what it is asking for. The player supplies
 *      the face; it is appended to F; the engine is run again from the green
 *      flag and — because it is a pure function of F and F's prefix has not
 *      changed — it retraces the identical race, consumes the identical faces
 *      in the identical order, arrives back at the same die, and goes one
 *      further.
 *
 *      THE PAUSE IS REAL. The engine genuinely stops at the die, not at a turn
 *      boundary, not after collecting a batch: `DiceNeeded` is thrown out of
 *      `Dice.d`, eight frames down, mid-challenge, mid-trouble-chain. What is
 *      re-done is arithmetic the player never sees, and the result is bit-exact
 *      because that is what purity means.
 *
 *      COST, MEASURED rather than guessed. A 50-turn race at Daytona with 43
 *      cars draws 837–1,065 faces and resolves in 15–50 ms. A resume replays
 *      the race so far, so the WORST single pause — the last die of the race —
 *      costs one full race, ~50 ms. Total compute over a hand-rolled race is
 *      about N/2 replays ≈ 25 seconds, spread across however long a human takes
 *      to physically roll nine hundred dice. Nobody will ever see it.
 *
 *      WHAT REPLAY BUYS ON TOP: `undo` is free and exact. A mis-typed face is
 *      popped off F and the race is retraced without it. There is no rollback
 *      machinery because there is no state to roll back — a design where an
 *      undo cannot be subtly wrong is worth a great deal in a game whose whole
 *      claim is an auditable ledger.
 *
 *      THE ONE THING IT REQUIRES, stated plainly so it is not forgotten: the
 *      race engine must stay pure. If someone later makes `runRace` read a
 *      clock, or memoise across calls, or mutate a module-level array, resume
 *      stops being exact and this file starts lying. `scripts/manualdice.ts`
 *      checks it every run — the replayed race is asserted equal to the
 *      single-shot race on the same faces — so the day that changes, the
 *      harness says so.
 */

import { DiceNeeded, type DiceRequest, type DieDraw } from './dice.ts';
import { runRace, type RaceOptions, type RaceResult, type RaceSetup } from './race.ts';
import type { TurnResult } from './turn.ts';

// ---------------------------------------------------------------------------
// What the screen is told
// ---------------------------------------------------------------------------

/** The engine has stopped and wants a face. */
export interface ManualDiceNeeded {
  status: 'need';
  /** Notation, sides, role, phase, and the faces already on the table. */
  request: DiceRequest;
  /** Every turn that COMPLETED before the engine stopped. */
  turns: readonly TurnResult[];
  /** Every face given so far, in order, with its provenance. */
  stream: readonly DieDraw[];
  /** One line a human can act on: "Roll the RED die (1–6)." */
  prompt: string;
}

/** The race ran to the checkered flag on the faces it was given. */
export interface ManualDiceDone {
  status: 'done';
  result: RaceResult;
}

export type ManualDiceStep = ManualDiceNeeded | ManualDiceDone;

/**
 * The sentence the dice-entry panel leads with.
 *
 * Deliberately says only what the engine actually knows at the seam — the
 * physical die to pick up, and the phase of the turn that asked. It does NOT
 * name a driver, because the dice object cannot know one (see `DieDraw.phase`),
 * and a plausible-looking driver name that was guessed would be exactly the
 * invented result this project bans.
 */
export function describeRequest(request: DiceRequest): string {
  const which =
    request.role && request.role !== 'd6' && request.role !== 'd10' && request.role !== 'dN'
      ? `the ${request.role.toUpperCase()} die`
      : `a d${request.sides}`;
  const reading =
    request.notation === 'dN'
      ? `a ${request.sides}-way selection`
      : request.notation === '2d10-sum'
        ? 'read as a 2–20 SUM'
        : request.notation === '2d6-sum'
      ? 'read as a 2–12 SUM'
      : request.notation === '11-66'
        ? 'read as an 11–66 two-digit code, tens then ones'
        : request.notation === 'race-cam'
          ? 'for the RACE CAM — black names the group, the two metallics read the track card'
          : request.notation === 'challenge'
            ? 'for a CHALLENGE — red, white and blue'
            : request.sides === 6
              ? 'a single d6'
              : request.sides === 10
                ? 'a single d10'
                : `a ${request.sides}-way pick`;
  const already =
    request.given.length > 0 ? ` Already on the table: ${request.given.join(', ')}.` : '';
  const where = request.phase ? ` (${request.phase})` : '';
  return `Roll ${which}, ${reading}.${already}${where}`;
}

// ---------------------------------------------------------------------------
// The session
// ---------------------------------------------------------------------------

/**
 * One hand-rolled race, played die by die.
 *
 * Usage is deliberately boring:
 *
 *     const session = new ManualRace(setup, options);
 *     let step = session.start();
 *     while (step.status === 'need') step = session.supply(faceFromTheTable());
 *     step.result;   // a RaceResult exactly like any other, plus a diceStream
 *
 * IMMUTABLE FROM THE OUTSIDE. The only state that exists is the face list; the
 * turns, the stream and the result are all recomputed from it. There is no
 * "current turn" to get out of step with anything, because there is no current
 * turn — there is a list of numbers and a pure function.
 */
export class ManualRace {
  private readonly setup: RaceSetup;
  private readonly options: RaceOptions;
  private readonly given: number[] = [];
  private last: ManualDiceStep | null = null;
  private completedTurns: TurnResult[] = [];

  constructor(setup: RaceSetup, options: RaceOptions = {}) {
    if (options.dice) {
      // Two sources of faces is one too many, and quietly preferring either one
      // would make the saved stream disagree with what the player typed.
      throw new Error(
        'ManualRace owns the dice feed — pass the starting faces to `start(faces)`, not in options.dice',
      );
    }
    this.setup = setup;
    this.options = options;
  }

  /** Every face entered so far, in table order. */
  get faces(): readonly number[] {
    return this.given;
  }

  /** Turns that finished before the current pause. Empty before `start`. */
  get turns(): readonly TurnResult[] {
    return this.completedTurns;
  }

  /**
   * Begin — or begin from faces already rolled, which is how a saved
   * half-finished race comes back to life: the faces ARE the save file.
   */
  start(faces: readonly number[] = []): ManualDiceStep {
    this.given.length = 0;
    this.given.push(...faces);
    return this.run();
  }

  /**
   * Hand the engine one or more faces and let it carry on.
   *
   * Faces are validated against the die actually being asked for BEFORE they
   * are accepted, so a 7 typed for a d6 is refused at the door rather than
   * silently shifting every later roll onto the wrong question. Only the FIRST
   * face can be checked this way — the ones after it answer questions the
   * engine has not asked yet — so the rest are checked by `Dice.d` as they are
   * drawn, which throws for the same reason.
   */
  supply(...faces: number[]): ManualDiceStep {
    const pending = this.last;
    if (!pending || pending.status !== 'need') {
      throw new Error('nothing is waiting on a die — call start() first, or the race is already finished');
    }
    if (faces.length === 0) throw new RangeError('supply() needs at least one face');
    const first = faces[0];
    if (!Number.isInteger(first) || first < 1 || first > pending.request.sides) {
      throw new RangeError(
        `${first} is not a face of the d${pending.request.sides} the engine is asking for` +
          (pending.request.role ? ` (${pending.request.role})` : ''),
      );
    }
    this.given.push(...faces);
    return this.run();
  }

  /**
   * Take back the last `n` faces.
   *
   * Free and exact, because the race is a function of the face list and nothing
   * else — see the header. Undoing past the start is a no-op rather than an
   * error: there is nothing there to be wrong about.
   */
  undo(n = 1): ManualDiceStep {
    for (let i = 0; i < n && this.given.length > 0; i++) this.given.pop();
    return this.run();
  }

  /**
   * "That's enough — run the rest."
   *
   * Finishes the race on the seeded PRNG from wherever the hand-rolled faces
   * stop. The crossover is recorded face by face in `diceStream`, and any TURN
   * that straddles it has its ledger rows marked `mixed` rather than `hand` —
   * see `DiceRecord.entry`. This is the ONLY way the engine ever rolls for you
   * once manual mode is on, and it only happens because you asked.
   */
  finishWithEngine(): RaceResult {
    return runRace(this.setup, {
      ...this.options,
      dice: { faces: [...this.given], exhausted: 'prng' },
    });
  }

  /**
   * Run the whole race on the faces held so far, and report where it got to.
   *
   * This is the replay. It is the only place `runRace` is called, and it always
   * starts from the green flag.
   */
  private run(): ManualDiceStep {
    const turns: TurnResult[] = [];
    try {
      const result = runRace(this.setup, {
        ...this.options,
        dice: { faces: [...this.given], exhausted: 'suspend' },
        onTurn: (t) => turns.push(t),
      });
      this.completedTurns = turns;
      this.last = { status: 'done', result };
      return this.last;
    } catch (err) {
      if (!(err instanceof DiceNeeded)) throw err;
      // Not an error — the pause. Everything the screen needs is on it.
      this.completedTurns = turns;
      this.last = {
        status: 'need',
        request: err.request,
        turns,
        stream: err.stream,
        prompt: describeRequest(err.request),
      };
      return this.last;
    }
  }
}

/**
 * Play a whole race from a list of faces already rolled, with no interaction.
 *
 * The non-interactive twin of `ManualRace`, and the thing the harness measures:
 * if this returns `{ status: 'need' }` the list was short, and if it returns a
 * result with `queueLeft > 0` the engine used fewer faces than it was given.
 * Both are countable, which is the whole point.
 */
export function runRaceOnFaces(
  setup: RaceSetup,
  faces: readonly number[],
  options: RaceOptions = {},
): ManualDiceStep {
  const session = new ManualRace(setup, options);
  return session.start(faces);
}
